/**
 * Custom Data Hooks - Centralized data fetching logic
 * Eliminates repetitive useState/useEffect patterns across pages
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/App';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// CORE HOOK: useApiData
// Generic hook for fetching any API data
// ============================================
export function useApiData(endpoint, options = {}) {
  const { token } = useAuth();
  const [data, setData] = useState(options.initialData || null);
  const [loading, setLoading] = useState(!options.lazy);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const abortControllerRef = useRef(null);

  const {
    lazy = false,           // Don't fetch on mount
    transform = null,       // Transform response data
    onSuccess = null,       // Callback on success
    onError = null,         // Callback on error
    cache = false,          // Enable caching
    cacheKey = endpoint,    // Cache key
    refetchInterval = null, // Auto-refetch interval (ms)
    showToast = false,      // Show toast on error
    dependencies = [],      // Additional dependencies for refetch
  } = options;

  const fetchData = useCallback(async (showLoading = true) => {
    if (!token || !endpoint) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    if (showLoading) setLoading(true);
    setError(null);

    try {
      const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let result = await response.json();
      
      // Apply transform if provided
      if (transform) {
        result = transform(result);
      }

      setData(result);
      setLastUpdated(new Date());
      
      if (onSuccess) onSuccess(result);
      
      // Cache result
      if (cache && cacheKey) {
        sessionStorage.setItem(`api_cache_${cacheKey}`, JSON.stringify({
          data: result,
          timestamp: Date.now()
        }));
      }

      return result;
    } catch (err) {
      if (err.name === 'AbortError') return;
      
      setError(err.message);
      if (showToast) {
        toast.error(`Failed to load data: ${err.message}`);
      }
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  }, [token, endpoint, transform, onSuccess, onError, cache, cacheKey, showToast]);

  // Initial fetch
  useEffect(() => {
    if (!lazy) {
      // Check cache first
      if (cache && cacheKey) {
        const cached = sessionStorage.getItem(`api_cache_${cacheKey}`);
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < 300000) { // 5 minute cache
            setData(cachedData);
            setLoading(false);
            return;
          }
        }
      }
      fetchData();
    }
  }, [lazy, fetchData, cache, cacheKey, ...dependencies]);

  // Auto-refetch interval
  useEffect(() => {
    if (!refetchInterval || lazy) return;
    
    const interval = setInterval(() => {
      fetchData(false); // Don't show loading on auto-refetch
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [refetchInterval, fetchData, lazy]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refetch: fetchData,
    setData, // Allow manual updates
    isStale: lastUpdated ? (Date.now() - lastUpdated.getTime() > 300000) : true
  };
}

// ============================================
// HOOK: useApiMutation
// For POST/PUT/DELETE operations
// ============================================
export function useApiMutation(endpoint, options = {}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    method = 'POST',
    onSuccess = null,
    onError = null,
    showSuccessToast = true,
    showErrorToast = true,
    successMessage = 'Operation successful',
    invalidateQueries = [], // Keys to invalidate from cache
  } = options;

  const mutate = useCallback(async (payload = null, overrideEndpoint = null) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const url = (overrideEndpoint || endpoint).startsWith('http') 
        ? (overrideEndpoint || endpoint) 
        : `${API_URL}${overrideEndpoint || endpoint}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: payload ? JSON.stringify(payload) : undefined
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (showSuccessToast) {
        toast.success(successMessage);
      }

      // Invalidate cache for related queries
      invalidateQueries.forEach(key => {
        sessionStorage.removeItem(`api_cache_${key}`);
      });

      if (onSuccess) onSuccess(result);
      
      return result;
    } catch (err) {
      setError(err.message);
      if (showErrorToast) {
        toast.error(`Error: ${err.message}`);
      }
      if (onError) onError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, endpoint, method, onSuccess, onError, showSuccessToast, showErrorToast, successMessage, invalidateQueries]);

  return { mutate, loading, error };
}

// ============================================
// HOOK: useDashboardData
// Specialized hook for dashboard metrics
// ============================================
export function useDashboardData() {
  const dashboardData = useApiData('/api/dashboard/overview', {
    cache: true,
    refetchInterval: 60000 // Refetch every minute
  });

  const complianceAlerts = useApiData('/api/compliance/alerts', {
    cache: true,
    initialData: []
  });

  return {
    dashboardData: dashboardData.data,
    complianceAlerts: complianceAlerts.data,
    loading: dashboardData.loading || complianceAlerts.loading,
    error: dashboardData.error || complianceAlerts.error,
    refetch: () => {
      dashboardData.refetch();
      complianceAlerts.refetch();
    }
  };
}

// ============================================
// HOOK: useCensusData
// Specialized hook for census/forecasting
// ============================================
export function useCensusData(weeks = 8) {
  const forecast = useApiData(`/api/census/forecast?weeks=${weeks}`, {
    cache: true,
    cacheKey: `census_forecast_${weeks}`
  });

  const historical = useApiData('/api/census/historical?limit=52', {
    cache: true,
    lazy: true
  });

  return {
    forecast: forecast.data,
    historical: historical.data,
    loading: forecast.loading,
    error: forecast.error,
    refetch: forecast.refetch,
    fetchHistorical: historical.refetch
  };
}

// ============================================
// HOOK: useProductivityData
// Specialized hook for RNCM productivity
// ============================================
export function useProductivityData() {
  const clinicians = useApiData('/api/productivity/clinicians', {
    initialData: [],
    cache: true
  });

  const capacity = useApiData('/api/capacity/rncm', {
    initialData: [],
    cache: true
  });

  const analytics = useApiData('/api/productivity/analytics', {
    cache: true
  });

  return {
    clinicians: clinicians.data,
    capacity: capacity.data,
    analytics: analytics.data,
    loading: clinicians.loading || capacity.loading || analytics.loading,
    error: clinicians.error || capacity.error || analytics.error,
    refetch: () => {
      clinicians.refetch();
      capacity.refetch();
      analytics.refetch();
    }
  };
}

// ============================================
// HOOK: useDataHealth
// Specialized hook for data health dashboard
// ============================================
export function useDataHealth() {
  const health = useApiData('/api/data-integrity/audit', {
    cache: true,
    cacheKey: 'data_health'
  });

  const syncStatus = useApiData('/api/dashboard/sync-status', {
    cache: true,
    cacheKey: 'sync_status'
  });

  const syncMutation = useApiMutation('/api/data-sync/full-sync', {
    successMessage: 'Data synced successfully',
    invalidateQueries: ['data_health', 'sync_status']
  });

  return {
    healthData: health.data,
    syncStatus: syncStatus.data,
    loading: health.loading,
    syncing: syncMutation.loading,
    error: health.error,
    refetch: health.refetch,
    syncData: syncMutation.mutate
  };
}

// ============================================
// HOOK: useTableData
// For paginated table data with sorting/filtering
// ============================================
export function useTableData(endpoint, options = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(options.defaultPageSize || 20);
  const [sortBy, setSortBy] = useState(options.defaultSortBy || null);
  const [sortOrder, setSortOrder] = useState(options.defaultSortOrder || 'desc');
  const [filters, setFilters] = useState(options.defaultFilters || {});

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', pageSize.toString());
    if (sortBy) {
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.set(key, value.toString());
      }
    });
    return params.toString();
  }, [page, pageSize, sortBy, sortOrder, filters]);

  const { data, loading, error, refetch } = useApiData(
    `${endpoint}?${buildQueryString()}`,
    { 
      ...options,
      dependencies: [page, pageSize, sortBy, sortOrder, JSON.stringify(filters)]
    }
  );

  return {
    data: data?.items || data || [],
    total: data?.total || 0,
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    loading,
    error,
    setPage,
    setPageSize,
    setSortBy,
    setSortOrder,
    setFilters,
    refetch,
    // Helpers
    nextPage: () => setPage(p => p + 1),
    prevPage: () => setPage(p => Math.max(1, p - 1)),
    resetFilters: () => setFilters(options.defaultFilters || {}),
    updateFilter: (key, value) => setFilters(f => ({ ...f, [key]: value }))
  };
}

// ============================================
// HOOK: useRefreshableData
// Simple hook with refresh button support
// ============================================
export function useRefreshableData(endpoint, options = {}) {
  const { data, loading, error, refetch, lastUpdated } = useApiData(endpoint, options);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
    toast.success('Data refreshed');
  }, [refetch]);

  return {
    data,
    loading,
    error,
    refreshing,
    lastUpdated,
    handleRefresh
  };
}

// ============================================
// HOOK: usePollingData
// For real-time data with polling
// ============================================
export function usePollingData(endpoint, interval = 30000, options = {}) {
  return useApiData(endpoint, {
    ...options,
    refetchInterval: interval
  });
}

// ============================================
// UTILITY: Prefetch data for navigation
// ============================================
export function prefetchData(endpoint, token) {
  if (!token) return;
  
  fetch(`${API_URL}${endpoint}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  })
    .then(res => res.json())
    .then(data => {
      sessionStorage.setItem(`api_cache_${endpoint}`, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    })
    .catch(() => {}); // Silent fail for prefetch
}

// ============================================
// UTILITY: Clear all cache
// ============================================
export function clearApiCache() {
  Object.keys(sessionStorage)
    .filter(key => key.startsWith('api_cache_'))
    .forEach(key => sessionStorage.removeItem(key));
}
